import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Checkbox,
  FormControlLabel,
  makeStyles,
  Typography
} from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

import OptionDivider from '../../Components/Dividers/OptionDivider';
import ConfirmationDialog from '../../Components/Dialogs/ConfirmationDialog';
import SocialButtons from '../../Components/Buttons/SocialButtons';
import SocialNameFields from './Components/SocialNameFields';
import useSocialRegistrationDialog from './hooks/useSocialRegistrationDialog';
import {
  buildGoogleParams,
  buildAppleParams,
  performAppleSignUp
} from './helpers/helpers';

import { AlertsDispatchContext } from '../../Contexts/AlertsContext';
import { UserContext, UserDispatchContext } from '../../Contexts/UserContext';

import { ROUTE_NAMES } from '../../Routes/Routes';
import { TERMS, TERMS_ROUTES } from '../Information/Information/Information';
import {
  AppleLoginAPI,
  RevokeAppleTokenAPI,
  GoogleLoginAPI
} from '../../API/SocialUserAPI';
import { GoogleSignUpAPI } from '../../API/UserAPI';
import { extractErrorMessage } from '../../Utils/Errors/Errors';

import { history } from '../../Routes/history';
import {
  GoogleClientContext,
  GoogleClientDispatchContext
} from '../../Contexts/GoogleClientContext';

import {
  AppleClientContext,
  AppleClientDispatchContext
} from '../../Contexts/AppleClientContext';

import { PrivacyPoliciesLink } from '../../Configs/Links';
import { updateUser } from '../../Utils/User/Actions';
import { SOCIAL_LOGIN_TYPE, SOCIAL_AUTH_STATUS } from '../../Enums/users';

const SocialLoginButtons = props => {
  const {
    closeDialog = null,
    hideSocialButtons = false,
    loading,
    loadingApple,
    setLoadingApple,
    loadingGoogle,
    setLoadingGoogle,
    hideGoogleButton = false,
    hideAppleButton = false,
    isUpdateUserPath = false,
    setTokenGoogle
  } = props;

  const classes = useStyles();

  const currentUser = useContext(UserContext);
  const setCurrentUser = useContext(UserDispatchContext);
  const setAlert = useContext(AlertsDispatchContext);
  const googleData = useContext(GoogleClientContext);
  const setGoogleData = useContext(GoogleClientDispatchContext);
  const appleData = useContext(AppleClientContext);
  const setAppleData = useContext(AppleClientDispatchContext);

  const [terms, setTerms] = useState(false);

  const {
    register,
    errors,
    formState,
    reset,
    confirmationDialog,
    setConfirmationDialog,
    newUserParams,
    openRegistrationDialog,
    applyDialogNames
  } = useSocialRegistrationDialog(isUpdateUserPath);

  const handleGoogleLogin = useCallback(
    async data => {
      const { encodedToken } = data;

      if (isUpdateUserPath) {
        openRegistrationDialog(
          SOCIAL_LOGIN_TYPE.Google,
          buildGoogleParams(data),
          setGoogleData
        );
        return;
      }

      setLoadingGoogle(true);

      const authResponse = await GoogleLoginAPI(encodedToken);

      if (!authResponse.success) {
        setAlert({
          type: 'error',
          message: extractErrorMessage(authResponse).message
        });
        setGoogleData(null);
        setLoadingGoogle(false);
        return;
      }

      const responseData = authResponse.data.data;
      const userType = responseData.userType;

      if (userType === SOCIAL_AUTH_STATUS.New) {
        openRegistrationDialog(
          SOCIAL_LOGIN_TYPE.Google,
          buildGoogleParams(data),
          setGoogleData
        );
        return;
      }

      const { token, appToken } = responseData;

      if (userType === SOCIAL_LOGIN_TYPE.Google) {
        setGoogleData(null);
        updateUser(setCurrentUser, token, appToken);
        if (!closeDialog) {
          history.replace('/');
        }
      }
    },
    [
      setAlert,
      setCurrentUser,
      closeDialog,
      setGoogleData,
      isUpdateUserPath,
      setLoadingGoogle,
      openRegistrationDialog
    ]
  );

  const handleAppleLogin = useCallback(
    async data => {
      const { givenName, familyName, encodedToken, code } = data;

      setLoadingApple(true);

      const authResponse = await AppleLoginAPI({
        idToken: encodedToken,
        code,
        firstName: givenName,
        lastName: familyName
      });

      if (!authResponse.success) {
        setAlert({
          type: 'error',
          message: extractErrorMessage(authResponse).message
        });
        setAppleData(null);
        setLoadingApple(false);
        return;
      }

      const responseData = authResponse.data.data;
      const userType = responseData.userType;

      if (userType === SOCIAL_AUTH_STATUS.New) {
        openRegistrationDialog(
          SOCIAL_LOGIN_TYPE.Apple,
          buildAppleParams(data),
          setAppleData
        );
        return;
      }

      const { token, appToken } = responseData;

      if (userType === SOCIAL_LOGIN_TYPE.Apple) {
        setAppleData(null);
        updateUser(setCurrentUser, token, appToken);
        if (!closeDialog) {
          history.replace('/');
        }
      }
    },
    [
      setAlert,
      setCurrentUser,
      closeDialog,
      setAppleData,
      setLoadingApple,
      openRegistrationDialog
    ]
  );

  const onChangeTerms = useCallback(() => {
    setTerms(!terms);
  }, [terms]);

  const handleTermsDialog = useCallback(
    async closed => {
      if (closed) {
        let closeMessage =
          'Se ha cerrado la ventana de términos y condiciones. Intenta nuevamente';
        if (newUserParams.nameRequired) {
          closeMessage =
            'Debes completar los datos requeridos para continuar. Intenta nuevamente';
        }
        setAlert({
          type: 'info',
          message: closeMessage
        });
        setLoadingGoogle(false);
        setTerms(false);
        reset({ firstName: '', lastName: '' });

        if (!hideAppleButton) {
          setLoadingApple(false);
        }

        if (newUserParams.apple) {
          const { idToken, code } = newUserParams.apple;
          await RevokeAppleTokenAPI({ idToken, code });
        }

        return;
      }

      let signUpResponse;

      if (newUserParams.google) {
        const googleParams = applyDialogNames(newUserParams.google);
        if (isUpdateUserPath) {
          // links this Google login to the existing account (authToken: null, fromUpdate: true)
          signUpResponse = await GoogleLoginAPI(
            googleParams.idToken,
            null,
            true
          );
        } else {
          signUpResponse = await GoogleSignUpAPI(googleParams);
        }
      } else if (newUserParams.apple) {
        signUpResponse = await performAppleSignUp(
          applyDialogNames(newUserParams.apple)
        );
      }

      if (!signUpResponse.success) {
        setAlert({
          type: 'error',
          message: extractErrorMessage(signUpResponse).message
        });
        setLoadingGoogle(false);
        if (!hideAppleButton) {
          setLoadingApple(false);
        }
        return { unmounting: false, closeDialog: true };
      }

      const { token, appToken } = signUpResponse.data.data;

      if (isUpdateUserPath) {
        setTokenGoogle(token);
        setGoogleData(null);
      }

      updateUser(setCurrentUser, token, appToken);
      setTerms(false);

      if (!closeDialog || isUpdateUserPath) {
        history.replace('/');
      }
      return { unmounting: true, closeDialog: false };
    },
    [
      hideAppleButton,
      newUserParams,
      setAlert,
      setCurrentUser,
      closeDialog,
      isUpdateUserPath,
      setLoadingGoogle,
      setLoadingApple,
      setTokenGoogle,
      setGoogleData,
      reset,
      applyDialogNames
    ]
  );

  useEffect(() => {
    if (googleData) {
      handleGoogleLogin(googleData);
    }
  }, [googleData, handleGoogleLogin]);

  useEffect(() => {
    if (appleData) {
      handleAppleLogin(appleData);
    }
  }, [appleData, handleAppleLogin]);

  let dialogTitle = 'Términos y condiciones';
  if (newUserParams.nameRequired) {
    dialogTitle = 'Completa tu registro';
  }

  let isDialogDisabled = !terms;
  if (newUserParams.nameRequired && !formState.isValid) {
    isDialogDisabled = true;
  }

  return (
    <>
      {confirmationDialog && (
        <ConfirmationDialog
          open={confirmationDialog}
          title={dialogTitle}
          content={
            <>
              {newUserParams.nameRequired && (
                <SocialNameFields register={register} errors={errors} />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    id="Login_dialog_terms"
                    checked={terms}
                    onClick={onChangeTerms}
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    disabled={Boolean(currentUser)}
                    value="checkedI"
                  />
                }
                label={
                  <Typography className={classes.termsText}>
                    He leído y acepto los{' '}
                    <a
                      className={classes.link}
                      href={`${ROUTE_NAMES.information}/${
                        TERMS_ROUTES[TERMS.introduction]
                      }`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      términos y condiciones
                    </a>{' '}
                    de uso, la{' '}
                    <a href={PrivacyPoliciesLink}>
                      política y aviso de privacidad
                    </a>{' '}
                    y autorizo el tratamiento de mis datos personales.
                  </Typography>
                }
              />
            </>
          }
          cancelButtonText="Cancelar"
          submitButtonText="Continuar"
          disabled={isDialogDisabled}
          setDialog={setConfirmationDialog}
          requestCallback={handleTermsDialog}
        />
      )}
      {!hideSocialButtons && (
        <>
          <SocialButtons
            googleLoading={loadingGoogle}
            appleLoading={loadingApple}
            emailLoading={loading}
            hideGoogleButton={hideGoogleButton}
            hideAppleButton={hideAppleButton}
          />
          <OptionDivider />
        </>
      )}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  title: {
    marginTop: 40,
    marginBottom: 20,
    fontSize: 24
  },
  link: {
    color: '#424242',
    fontWeight: 500
  },
  termsText: {
    fontSize: 11,
    [theme.breakpoints.up('sm')]: {
      fontSize: 12
    }
  }
}));

export default SocialLoginButtons;
